/**
 * 基准面管理器。
 * 负责创建、管理所有基准面，以及基准面视图的进入/退出和当前参考面状态维护。
 */
import * as THREE from 'three';
import {ReferencePlane} from '../reference/ReferencePlane';
import {DatumPlane, DefaultDatumPlaneKind, type DatumPlaneOptions} from '../reference/DatumPlane';
import { ReferenceCreationContext } from '../reference/ReferenceCreationContext';
import type {CameraManager} from '../camera/CameraManager';
import {
    isPositiveFiniteDisplaySize,
    resolveDefaultDatumPlaneDisplaySize,
} from '../reference/reference-plane/ReferencePlaneDisplaySizePolicy';
import {
    DATUM_PLANE_HISTORY_ENTITY_ID,
    type DatumPlaneHistoryRecord,
    type DatumPlaneHistorySnapshot,
    type DatumPlaneHistoryVector3,
    type UserCreatedDatumPlaneTimelineEntry,
} from '../reference/DatumPlaneHistoryTypes';
import { ViewMode } from '../viewer/ViewMode';
import type {Viewer} from '../viewer/Viewer';

//------ 基准面JSON序列化 开始------
/**
 * 基准面 JSON 协议版本。
 */
export enum DatumPlaneJSONSchemaVersion {
    /** 初始版本：保存 DatumPlaneManager 可恢复的正式基准面 plain data。 */
    V1 = 1,
}

/**
 * 基准面管理器 JSON 文档。
 *
 * 设计说明：
 * 1. 该结构复用历史快照 plain data，避免 Manager 内维护第二套并行持久化模型；
 * 2. 默认前视、上视、右视基准面仍由 Manager 构造阶段创建，不进入 JSON；
 * 3. 当前不接入离线包导入导出，只供上层后续统一编排。
 */
export interface DatumPlaneDocumentJSON extends DatumPlaneHistorySnapshot {
    /** JSON 协议版本。 */
    readonly schemaVersion: DatumPlaneJSONSchemaVersion.V1;
}
//------ 基准面JSON序列化 结束------

//------ 默认基准面命名 开始------
/**
 * 默认内置基准面名称。
 */
enum DefaultDatumPlaneName {
    /** 前视基准面。 */
    Front = '前视基准面',
    /** 上视基准面。 */
    Top = '上视基准面',
    /** 右视基准面。 */
    Right = '右视基准面',
}
//------ 默认基准面命名 结束------


/**
 * 基准面管理器配置选项。
 */
//------ 统一相机位置与默认基准面尺寸 开始------
export interface DatumPlaneManagerOptions {
    /** 基准面默认大小。 */
    defaultSize?: number;
    /** 基准面默认宽度。 */
    defaultWidth?: number;
    /** 基准面默认高度。 */
    defaultHeight?: number;
    /** 基准面默认面片颜色。 */
    defaultColor?: number;
    /** 基准面默认边框颜色。 */
    defaultBorderColor?: number;
    /** 是否自动创建默认三个基准面。 */
    createDefaultPlanes?: boolean;
}
//------ 统一相机位置与默认基准面尺寸 结束------

/**
 * 基准面管理器。
 * 职责：基准面 CRUD、视图模式管理、当前参考面状态维护。
 */
export class DatumPlaneManager {
    //------ 统一相机位置与默认基准面尺寸 开始------
    /** 默认基准面正视视距从原本的 300 场景单位（米）修正为 0.3（300 mm），使之适配毫米级模型视角。 */
    private static readonly DEFAULT_DATUM_PLANE_VIEW_DISTANCE = 0.3;
    //------ 统一相机位置与默认基准面尺寸 结束------

    /** 轴向退化判定容差。 */
    private static readonly AXIS_EPSILON = 1e-10;

    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 场景引用。 */
    private readonly scene: THREE.Scene;
    /** 基准面集合。 */
    private planes: Map<number, DatumPlane> = new Map();
    /** 配置选项。 */
    private options: Required<DatumPlaneManagerOptions>;
    /** 是否全部可见。 */
    private allVisible: boolean = false;
    /** 临时可见的基准面集合。 */
    private temporaryVisiblePlanes: Set<number> = new Set();
    /** 相机管理器引用（用于视图切换时操作相机）。 */
    private cameraManager: CameraManager | null = null;
    /** 当前草图参考面。 */
    public currentReferencePlane: ReferencePlane | null = null;
    /** 当前视图模式。 */
    public viewMode: ViewMode = ViewMode.VIEW_3D;
    /** 用户创建基准面名称前缀。 */
    private static readonly CUSTOM_PLANE_NAME_PREFIX = '基准面';
    /**
     * 用户手动创建基准面的设计树时间线登记数组。
     *
     * 设计说明：
     * 1. 与 `planes` Map 解耦，专门服务设计树按创建时间排序；
     * 2. 默认前/上/右视基准面不会写入该数组；
     * 3. Undo/Redo 恢复时通过历史记录中的 `createdAt` 重建该数组。
     */
    private readonly userCreatedPlaneTimeline: UserCreatedDatumPlaneTimelineEntry[] = [];

    constructor(viewer: Viewer, options: DatumPlaneManagerOptions = {}) {
        this.viewer = viewer;
        this.scene = viewer.scene;
        //------ 统一相机位置与默认基准面尺寸 开始------
        // 将缺省兜底尺寸与长宽全部修正为毫米级（0.2m, 0.18m, 0.11m），以适配毫米级建模要求。
        // 若显式传入了 defaultSize 却未指定长宽，则根据 defaultSize 的比例换算长宽。
        const defaultSize = options.defaultSize ?? 0.2;
        const fallbackSize = resolveDefaultDatumPlaneDisplaySize(defaultSize);
        this.options = {
            defaultSize,
            defaultWidth: options.defaultWidth ?? (options.defaultSize !== undefined ? fallbackSize.width : 0.18),
            defaultHeight: options.defaultHeight ?? (options.defaultSize !== undefined ? fallbackSize.height : 0.11),
            defaultColor: options.defaultColor ?? 0x6dc5f6,
            defaultBorderColor: options.defaultBorderColor ?? 0x4a9eff,
            createDefaultPlanes: options.createDefaultPlanes ?? true,
        };
        //------ 统一相机位置与默认基准面尺寸 结束------

        if (this.options.createDefaultPlanes) {
            this.createDefaultPlanes();
        }
    }

    /**
     * 创建默认的三个基准面
     */
    private createDefaultPlanes(): void {
        //------ 统一相机位置与默认基准面尺寸 开始------
        // 优先使用明确配置的默认宽度与高度，若未定义则回退到 defaultSize 转换逻辑
        const width = this.options.defaultWidth ?? resolveDefaultDatumPlaneDisplaySize(this.options.defaultSize).width;
        const height = this.options.defaultHeight ?? resolveDefaultDatumPlaneDisplaySize(this.options.defaultSize).height;
        //------ 统一相机位置与默认基准面尺寸 结束------

        // 前视基准面 (XY平面, Z=0)
        const frontPlane = new DatumPlane({
            creationContext: ReferenceCreationContext.DefaultBuiltIn,
            //------ 默认基准面稳定身份 开始------
            defaultPlaneKind: DefaultDatumPlaneKind.Front,
            //------ 默认基准面稳定身份 结束------
            name: DefaultDatumPlaneName.Front,
            normal: new THREE.Vector3(0, 0, 1),
            originWorld: new THREE.Vector3(0, 0, 0),
            color: this.options.defaultColor,
            borderColor: this.options.defaultBorderColor,
            width: width,
            height: height,
            visible: false,
            opacity: 0.3,
        });
        this.addPlane(frontPlane);

        // 上视基准面 (XZ平面, Y=0)
        const topPlane = new DatumPlane({
            creationContext: ReferenceCreationContext.DefaultBuiltIn,
            //------ 默认基准面稳定身份 开始------
            defaultPlaneKind: DefaultDatumPlaneKind.Top,
            //------ 默认基准面稳定身份 结束------
            name: DefaultDatumPlaneName.Top,
            normal: new THREE.Vector3(0, 1, 0),
            originWorld: new THREE.Vector3(0, 0, 0),
            color: this.options.defaultColor,
            borderColor: this.options.defaultBorderColor,
            width: width,
            height: height,
            visible: false,
            opacity: 0.3,
        });
        this.addPlane(topPlane);

        // 右视基准面 (YZ平面, X=0)
        const rightPlane = new DatumPlane({
            creationContext: ReferenceCreationContext.DefaultBuiltIn,
            //------ 默认基准面稳定身份 开始------
            defaultPlaneKind: DefaultDatumPlaneKind.Right,
            //------ 默认基准面稳定身份 结束------
            name: DefaultDatumPlaneName.Right,
            normal: new THREE.Vector3(1, 0, 0),
            originWorld: new THREE.Vector3(0, 0, 0),
            color: this.options.defaultColor,
            borderColor: this.options.defaultBorderColor,
            width: width,
            height: height,
            visible: false,
            opacity: 0.3,
        });
        this.addPlane(rightPlane);
    }

    /**
     * 添加基准面
     */
    addPlane(plane: DatumPlane): void {
        this.planes.set(plane.id, plane);
        plane.addToScene(this.scene);
        if (!plane.isTemporary && plane.creationContext !== ReferenceCreationContext.DefaultBuiltIn) {
            this.viewer.notifyDatumPlaneDefinitionChanged?.(plane.id);
        }
    }

    /**
     * 创建新的基准面
     */
    createPlane(options: DatumPlaneOptions): DatumPlane {
        const resolvedName = (
            typeof options.name === 'string' && options.name.trim().length > 0
                ? options.name
                : this.generateNextCustomPlaneName()
        );
        //------ 统一相机位置与默认基准面尺寸 开始------
        const fallbackDisplaySize = resolveDefaultDatumPlaneDisplaySize(this.options.defaultSize);
        const hasExplicitSquareSize = isPositiveFiniteDisplaySize(options.size);
        const resolvedWidth = options.width ?? (
            hasExplicitSquareSize ? options.size : (this.options.defaultWidth ?? fallbackDisplaySize.width)
        );
        const resolvedHeight = options.height ?? (
            hasExplicitSquareSize ? options.size : (this.options.defaultHeight ?? fallbackDisplaySize.height)
        );
        //------ 统一相机位置与默认基准面尺寸 结束------
        const plane = new DatumPlane({
            ...options,
            name: resolvedName,
            color: options.color ?? this.options.defaultColor,
            borderColor: options.borderColor ?? this.options.defaultBorderColor,
            size: undefined,
            width: resolvedWidth,
            height: resolvedHeight,
            visible: options.visible ?? true,
            opacity: options.opacity ?? 0.3,
        });
        this.addPlane(plane);
        if (!plane.isTemporary && plane.creationContext !== ReferenceCreationContext.DefaultBuiltIn) {
            this.registerUserCreatedPlaneTimelineEntry(plane.id, options.createdAt);
        }
        return plane;
    }

    /**
     * 读取用户手动创建基准面的创建时间戳。
     *
     * @param planeId 基准面数值 ID。
     * @returns 创建时间戳（毫秒）；非用户创建基准面时返回 `undefined`。
     */
    public getUserCreatedPlaneCreatedAt(planeId: number): number | undefined {
        return this.userCreatedPlaneTimeline.find((entry) => entry.planeId === planeId)?.createdAt;
    }

    /**
     * 登记用户手动创建基准面的设计树时间线项。
     *
     * @param planeId 基准面数值 ID。
     * @param createdAt 创建时间戳；缺省时写入当前时间。
     */
    private registerUserCreatedPlaneTimelineEntry(planeId: number, createdAt?: number): void {
        const existingEntry = this.userCreatedPlaneTimeline.find((entry) => entry.planeId === planeId);
        if (existingEntry) {
            return;
        }
        this.userCreatedPlaneTimeline.push({
            planeId,
            createdAt: Number.isFinite(createdAt) ? Number(createdAt) : Date.now(),
        });
    }

    /**
     * 移除用户手动创建基准面的设计树时间线项。
     *
     * @param planeId 基准面数值 ID。
     */
    private unregisterUserCreatedPlaneTimelineEntry(planeId: number): void {
        const entryIndex = this.userCreatedPlaneTimeline.findIndex((entry) => entry.planeId === planeId);
        if (entryIndex >= 0) {
            this.userCreatedPlaneTimeline.splice(entryIndex, 1);
        }
    }

    //------ 基准面编辑特征 开始------
    /**
     * 使用同一基准面 ID 替换已有基准面。
     *
     * 设计说明：
     * 1. SolidWorks 的“编辑特征”会修改原基准面，而不是创建新节点；
     * 2. DatumPlane 的计算来源在构造后保持不可变，因此这里通过受控替换重建对象；
     * 3. 替换过程保留原 ID、名称和可见性，保证设计树节点身份与用户命名不丢失。
     *
     * @param planeId 需要更新的基准面 ID。
     * @param options 新基准面几何与计算来源。
     * @returns 更新成功后的基准面；未找到原基准面时返回 `null`。
     */
    public updatePlane(planeId: number, options: DatumPlaneOptions): DatumPlane | null {
        const previousPlane = this.planes.get(planeId);
        if (!previousPlane) {
            return null;
        }

        const previousDisplay = previousPlane.getDisplaySnapshot();
        const nextPlane = new DatumPlane({
            ...options,
            id: planeId,
            //------ 默认基准面稳定身份 开始------
            defaultPlaneKind: previousPlane.defaultPlaneKind,
            //------ 默认基准面稳定身份 结束------
            name: previousPlane.name,
            color: options.color ?? previousDisplay.color,
            borderColor: options.borderColor ?? previousDisplay.borderColor,
            width: options.width ?? previousDisplay.width,
            height: options.height ?? previousDisplay.height,
            visible: options.visible ?? previousPlane.isVisible(),
            opacity: options.opacity ?? previousDisplay.opacity,
        });

        if (this.currentReferencePlane?.id === planeId) {
            this.currentReferencePlane = nextPlane;
        }

        previousPlane.removeFromScene(this.scene);
        previousPlane.dispose();
        this.planes.set(planeId, nextPlane);
        nextPlane.addToScene(this.scene);
        if (!nextPlane.isTemporary) this.viewer.notifyDatumPlaneDefinitionChanged?.(planeId);
        this.viewer.requestRender();
        return nextPlane;
    }
    //------ 基准面编辑特征 结束------

    /**
     * 生成下一个用户创建基准面名称。
     *
     * @returns 形如“基准面1”的名称。
     */
    private generateNextCustomPlaneName(): string {
        const prefix = DatumPlaneManager.CUSTOM_PLANE_NAME_PREFIX;
        let nextIndex = 1;
        const usedNames = new Set(this.getAllPlanes().map((plane) => plane.name));
        while (usedNames.has(`${prefix}${nextIndex}`)) {
            nextIndex += 1;
        }
        return `${prefix}${nextIndex}`;
    }

    /**
     * 移除基准面
     */
    removePlane(planeId: number): void {
        const plane = this.planes.get(planeId);
        if (plane) {
            /**
             * 若当前正在该平面法向视图中，必须先走标准退出流程，
             * 否则相机状态与当前参考面都会残留在已释放资源上。
             */
            if (this.currentReferencePlane?.id === planeId && this.viewMode === ViewMode.DATUM_PLANE) {
                this.exitView();
            } else if (this.currentReferencePlane?.id === planeId) {
                this.currentReferencePlane = null;
            }
            plane.removeFromScene(this.scene);
            plane.dispose();
            this.planes.delete(planeId);
            if (!plane.isTemporary) this.viewer.notifyDatumPlaneDefinitionChanged?.(planeId);
            this.temporaryVisiblePlanes.delete(planeId);
            if (!plane.isTemporary && plane.creationContext !== ReferenceCreationContext.DefaultBuiltIn) {
                this.unregisterUserCreatedPlaneTimelineEntry(planeId);
            }
        }
    }

    /**
     * 获取基准面
     */
    getPlane(planeId: number): DatumPlane | undefined {
        return this.planes.get(planeId);
    }

    /**
     * 重命名基准面。
     *
     * @param planeId 基准面 ID。
     * @param nextName 新名称。
     * @returns 是否重命名成功。
     */
    public renamePlane(planeId: number, nextName: string): boolean {
        const plane = this.planes.get(planeId);
        const normalizedName = nextName.trim();
        if (!plane || normalizedName.length === 0) {
            return false;
        }
        plane.setName(normalizedName);
        this.viewer.requestRender();
        return true;
    }

    /**
     * 获取所有基准面
     */
    getAllPlanes(): DatumPlane[] {
        return Array.from(this.planes.values());
    }

    //------ 实体面草图宿主面保留 开始------
    /**
     * 获取所有持久基准面。
     *
     * 设计说明：
     * 1. 实体面草图退出后会保留隐藏的临时宿主面，供后续编辑草图恢复绘制投影上下文；
     * 2. 该宿主面不是用户创建基准面，不能进入设计树基准面列表、全局基准面显隐和基准面历史；
     * 3. 因此对外展示和持久化链路统一使用该过滤入口，避免各处重复判断 `isTemporary`。
     *
     * @returns 不包含草图宿主临时面的基准面集合。
     */
    getAllPersistentPlanes(): DatumPlane[] {
        return this.getAllPlanes().filter((plane) => !plane.isTemporary);
    }
    //------ 实体面草图宿主面保留 结束------

    //------ 基准面JSON序列化 开始------
    /**
     * 导出当前基准面管理器 JSON。
     *
     * 设计说明：
     * 1. 默认三基准面是系统内置对象，不进入 JSON，恢复时继续由构造函数创建；
     * 2. 返回值只包含 plain data，不包含 Three.js Group、Mesh、材质、标签或场景引用；
     * 3. 当前 API 不写入 `.puppy`，只提供 Manager 级序列化能力。
     *
     * @returns 可持久化的基准面 JSON 文档。
     */
    public toJSON(): DatumPlaneDocumentJSON {
        const snapshot = this.createHistorySnapshot();

        /**
         * 通过 JSON 克隆主动移除 `undefined` 与运行时原型，保证调用方拿到的就是可写盘数据。
         */
        return this.cloneDatumPlaneDocumentJSON({
            schemaVersion: DatumPlaneJSONSchemaVersion.V1,
            ...snapshot,
        });
    }

    /**
     * 从 JSON 恢复基准面管理器状态。
     *
     * 设计说明：
     * 1. 先校验协议版本与必要字段，避免无效 JSON 进入对象重建流程；
     * 2. 复用 `restoreHistorySnapshot()` 完成删除旧用户基准面、重建对象、清理选择和刷新渲染；
     * 3. 当前只恢复 Manager 掌握的正式基准面对象，不接入项目导入导出。
     *
     * @param data 外部传入的基准面 JSON。
     */
    public fromJSON(data: unknown): void {
        this.assertDatumPlaneDocumentJSON(data);
        const document = this.cloneDatumPlaneDocumentJSON(data);

        /**
         * Manager 的恢复入口只消费历史快照字段，`schemaVersion` 在校验后不再传入。
         */
        this.restoreHistorySnapshot({
            managerId: document.managerId,
            planes: document.planes,
        });
    }

    /**
     * 克隆基准面 JSON 文档。
     *
     * @param document 来源 JSON 文档。
     * @returns 去除运行时引用后的 JSON 文档。
     */
    private cloneDatumPlaneDocumentJSON(document: DatumPlaneDocumentJSON): DatumPlaneDocumentJSON {
        return JSON.parse(JSON.stringify(document)) as DatumPlaneDocumentJSON;
    }

    /**
     * 断言未知值是基准面 JSON 文档。
     *
     * @param data 外部输入。
     */
    private assertDatumPlaneDocumentJSON(data: unknown): asserts data is DatumPlaneDocumentJSON {
        if (!this.isRecord(data)) {
            throw new Error('基准面 JSON 必须是对象。');
        }
        if (data.schemaVersion !== DatumPlaneJSONSchemaVersion.V1) {
            throw new Error(`不支持的基准面 JSON 版本：${String(data.schemaVersion)}`);
        }
        if (data.managerId !== DATUM_PLANE_HISTORY_ENTITY_ID) {
            throw new Error(`基准面 JSON.managerId 不匹配：${String(data.managerId)}`);
        }
        if (!Array.isArray(data.planes)) {
            throw new Error('基准面 JSON.planes 必须是数组。');
        }

        /**
         * 正式面对象恢复依赖平面坐标系和显示尺寸，因此这里逐条校验，避免恢复中途才出现难定位错误。
         */
        const planeIds = new Set<number>();
        data.planes.forEach((record, index) => {
            this.assertDatumPlaneHistoryRecord(record, index);
            if (planeIds.has(record.planeId)) {
                throw new Error(`基准面 JSON.planes[${index}].planeId 重复：${record.planeId}`);
            }
            planeIds.add(record.planeId);
        });
    }

    /**
     * 断言未知值是单条基准面历史记录。
     *
     * @param value 外部输入。
     * @param index 数组下标。
     */
    private assertDatumPlaneHistoryRecord(
        value: unknown,
        index: number,
    ): asserts value is DatumPlaneHistoryRecord {
        if (!this.isRecord(value)) {
            throw new Error(`基准面 JSON.planes[${index}] 必须是对象。`);
        }
        if (!Number.isFinite(value.planeId)) {
            throw new Error(`基准面 JSON.planes[${index}].planeId 必须是有限数字。`);
        }
        if (typeof value.name !== 'string' || value.name.trim().length === 0) {
            throw new Error(`基准面 JSON.planes[${index}].name 必须是非空字符串。`);
        }
        if (!this.isReferenceCreationContext(value.creationContext)) {
            throw new Error(`基准面 JSON.planes[${index}].creationContext 不合法。`);
        }
        if (value.creationContext === ReferenceCreationContext.DefaultBuiltIn) {
            throw new Error(`基准面 JSON.planes[${index}].creationContext 不允许恢复默认内置对象。`);
        }

        this.assertDatumPlaneHistoryVector3(value.originWorld, `基准面 JSON.planes[${index}].originWorld`);
        this.assertDatumPlaneHistoryVector3(value.normal, `基准面 JSON.planes[${index}].normal`);
        this.assertDatumPlaneHistoryVector3(value.xAxis, `基准面 JSON.planes[${index}].xAxis`);
        this.assertDatumPlaneHistoryVector3(value.yAxis, `基准面 JSON.planes[${index}].yAxis`);

        if (!Number.isFinite(value.width) || Number(value.width) <= 0) {
            throw new Error(`基准面 JSON.planes[${index}].width 必须是正有限数字。`);
        }
        if (!Number.isFinite(value.height) || Number(value.height) <= 0) {
            throw new Error(`基准面 JSON.planes[${index}].height 必须是正有限数字。`);
        }
        if (!Number.isFinite(value.color)) {
            throw new Error(`基准面 JSON.planes[${index}].color 必须是有限数字。`);
        }
        if (typeof value.visible !== 'boolean') {
            throw new Error(`基准面 JSON.planes[${index}].visible 必须是布尔值。`);
        }
        if (!Number.isFinite(value.opacity)) {
            throw new Error(`基准面 JSON.planes[${index}].opacity 必须是有限数字。`);
        }
        if (
            value.calculationSource !== undefined
            && value.calculationSource !== null
            && !this.isRecord(value.calculationSource)
        ) {
            throw new Error(`基准面 JSON.planes[${index}].calculationSource 必须是对象或 null。`);
        }
        if (
            value.createdAt !== undefined
            && !Number.isFinite(value.createdAt)
        ) {
            throw new Error(`基准面 JSON.planes[${index}].createdAt 必须是有限数字。`);
        }
    }

    /**
     * 断言未知值是基准面历史三维向量。
     *
     * @param value 外部输入。
     * @param fieldName 字段名。
     */
    private assertDatumPlaneHistoryVector3(
        value: unknown,
        fieldName: string,
    ): asserts value is DatumPlaneHistoryVector3 {
        if (
            !Array.isArray(value)
            || value.length !== 3
            || value.some((item) => !Number.isFinite(item))
        ) {
            throw new Error(`${fieldName} 必须是三个有限数字。`);
        }
    }

    /**
     * 判断未知值是否为普通记录对象。
     *
     * @param value 待判断值。
     * @returns 是否为普通记录对象。
     */
    private isRecord(value: unknown): value is Record<string, unknown> {
        return Boolean(value && typeof value === 'object' && !Array.isArray(value));
    }

    /**
     * 判断未知值是否为合法创建来源。
     *
     * @param value 待判断值。
     * @returns 是否为合法创建来源。
     */
    private isReferenceCreationContext(value: unknown): value is ReferenceCreationContext {
        return Object.values(ReferenceCreationContext).includes(value as ReferenceCreationContext);
    }
    //------ 基准面JSON序列化 结束------

    //------ 基准面撤销重做 开始------
    /**
     * 捕获用户创建基准面的历史快照。
     *
     * 设计说明：
     * 1. 默认前/上/右基准面是系统内置对象，不进入模型历史；
     * 2. 用户创建的零件态和装配体态基准面需要保存数值 ID，保证 Redo 后引用 ID 不变化；
     * 3. 快照只保存 plain data，不保存 THREE.Object3D、材质或几何体。
     *
     * @returns 基准面历史快照。
     */
    public createHistorySnapshot(): DatumPlaneHistorySnapshot {
        return {
            managerId: DATUM_PLANE_HISTORY_ENTITY_ID,
            planes: this.getAllPersistentPlanes()
                .filter((plane) => plane.creationContext !== ReferenceCreationContext.DefaultBuiltIn)
                .map((plane) => this.createHistoryRecordFromPlane(plane)),
        };
    }

    /**
     * 按历史快照恢复用户创建的基准面。
     *
     * @param snapshot 基准面历史快照。
     */
    public restoreHistorySnapshot(snapshot: DatumPlaneHistorySnapshot): void {
        this.getAllPlanes()
            .filter((plane) => !plane.isTemporary && plane.creationContext !== ReferenceCreationContext.DefaultBuiltIn)
            .forEach((plane) => {
                this.removePlane(plane.id);
            });
        this.userCreatedPlaneTimeline.length = 0;

        snapshot.planes.forEach((record, index) => {
            this.createPlane({
                id: record.planeId,
                creationContext: record.creationContext,
                name: record.name,
                originWorld: this.createVectorFromHistory(record.originWorld),
                normal: this.createVectorFromHistory(record.normal),
                xAxis: this.createVectorFromHistory(record.xAxis),
                yAxis: this.createVectorFromHistory(record.yAxis),
                width: record.width,
                height: record.height,
                color: record.color,
                visible: record.visible,
                opacity: record.opacity,
                calculationSource: record.calculationSource ?? null,
                createdAt: Number.isFinite(record.createdAt)
                    ? Number(record.createdAt)
                    : Date.now() + index,
            });
        });

        this.temporaryVisiblePlanes.clear();
        const persistentPlanes = this.getAllPersistentPlanes();
        this.allVisible = persistentPlanes.length > 0
            && persistentPlanes.every((plane) => plane.isVisible());
        this.viewer.entitySelectionManager?.clearSelectionProgrammatically();
        this.viewer.requestRender();
    }

    /**
     * 从基准面创建历史记录。
     *
     * @param plane 基准面对象。
     * @returns 基准面历史记录。
     */
    private createHistoryRecordFromPlane(plane: DatumPlane): DatumPlaneHistoryRecord {
        const display = plane.getDisplaySnapshot();

        return {
            planeId: plane.id,
            name: plane.name,
            creationContext: plane.creationContext,
            originWorld: this.createHistoryVector(plane.originWorld),
            normal: this.createHistoryVector(plane.normal),
            xAxis: this.createHistoryVector(plane.xAxis),
            yAxis: this.createHistoryVector(plane.yAxis),
            width: display.width,
            height: display.height,
            color: display.color,
            visible: display.visible,
            opacity: display.opacity,
            calculationSource: plane.getCalculationSource(),
            createdAt: this.getUserCreatedPlaneCreatedAt(plane.id),
        };
    }

    /**
     * 创建历史向量。
     *
     * @param vector Three.js 向量。
     * @returns plain data 向量。
     */
    private createHistoryVector(vector: THREE.Vector3): DatumPlaneHistoryVector3 {
        return [vector.x, vector.y, vector.z];
    }

    /**
     * 从历史向量创建 Three.js 向量。
     *
     * @param vector 历史向量。
     * @returns Three.js 向量。
     */
    private createVectorFromHistory(vector: DatumPlaneHistoryVector3): THREE.Vector3 {
        return new THREE.Vector3(vector[0], vector[1], vector[2]);
    }
    //------ 基准面撤销重做 结束------

    /**
     * 切换所有基准面的显示/隐藏
     */
    toggleAllVisible(): boolean {
        this.allVisible = !this.allVisible;
        this.temporaryVisiblePlanes.clear();
        this.getAllPersistentPlanes().forEach((plane) => {
            plane.setVisible(this.allVisible);
        });
        return this.allVisible;
    }


    /**
     * 设置单个基准面可见性
     */
    setPlaneVisible(planeId: number, visible: boolean): void {
        const plane = this.planes.get(planeId);
        if (!plane) return;
        plane.setVisible(visible);
        this.temporaryVisiblePlanes.delete(planeId);
        if (!visible) {
            this.allVisible = false;
            return;
        }
        const persistentPlanes = this.getAllPersistentPlanes();
        if (persistentPlanes.length === 0) {
            this.allVisible = false;
            return;
        }
        this.allVisible = persistentPlanes.every(item => item.isVisible());
    }

    /**
     * 临时显示基准面（用于特征树悬停）
     */
    showPlaneTemporarily(planeId: number): void {
        const plane = this.planes.get(planeId);
        if (!plane) return;
        if (!plane.isVisible()) {
            plane.setVisible(true);
            this.temporaryVisiblePlanes.add(planeId);
        }
    }

    /**
     * 隐藏全部基准面，并清除临时显示状态。
     */
    hideAllPlanes(): void {
        this.temporaryVisiblePlanes.clear();
        this.allVisible = false;
        this.planes.forEach((plane) => {
            plane.setVisible(false);
        });
    }

    /**
     * 取消临时显示基准面
     */
    hideTemporaryPlane(planeId: number): void {
        const plane = this.planes.get(planeId);
        if (!plane) return;
        if (this.temporaryVisiblePlanes.has(planeId)) {
            plane.setVisible(false);
            this.temporaryVisiblePlanes.delete(planeId);
        }
    }


    /**
     * 设置相机管理器引用。
     * 需要在初始化阶段注入，以便基准面视图切换时操作相机。
     * @param cameraManager 相机管理器实例。
     */
    setCameraManager(cameraManager: CameraManager): void {
        this.cameraManager = cameraManager;
    }

    /**
     * 进入基准面视图。
     *
     * 设计说明：
     * 1. 进入草图时允许把相机对齐到草图平面，保证绘制体验；
     * 2. 但模型命令不应保存视图状态并在退出时强制恢复，否则会污染独立的视图历史语义；
     * 3. 因此这里只同步当前参考面与视图模式，不再记录“进入前相机”。
     *
     * @param input 输入参数。
     */
    enterView(input: {
        planeId: number;
        cameraFrame?: {
            cameraPosition: THREE.Vector3;
            target: THREE.Vector3;
            up: THREE.Vector3;
        };
    }): void {
        const {planeId, cameraFrame} = input;
        const managedPlane = this.getPlane(planeId);
        /**
         * `.puppy` 恢复出的草图参考面是纯数学 ReferencePlane，不会重复注册为可视 DatumPlane。
         * 右键编辑该草图时，恢复入口已经把它写入 currentReferencePlane，因此正视入口应允许
         * 按同一 ID 复用当前参考面，避免因缺少可视面对象而丢失绘制投影上下文。
         */
        const plane = managedPlane ?? (
            this.currentReferencePlane?.id === planeId
                ? this.currentReferencePlane
                : null
        );
        if (!plane) {
            return;
        }
        // 设置当前参考面和视图模式，但不保存旧相机，避免退出草图时回跳视角。
        this.currentReferencePlane = plane;
        this.viewMode = ViewMode.DATUM_PLANE;
        if (!this.cameraManager) {
            return;
        }
        if (cameraFrame) {
            this.cameraManager.lookAtTarget(cameraFrame.cameraPosition, cameraFrame.target, cameraFrame.up);
            this.cameraManager.applyMouseMode();
            return;
        }
        //------ 基准面正视相机旋转修正 开始------
        const resolvedCameraFrame = this.resolveDatumPlaneCameraFrame(plane);
        if (managedPlane) {
            this.focusCameraToDatumPlaneBounds(managedPlane, resolvedCameraFrame);
        } else {
            /**
             * 纯数学参考面没有可视 Object3D 包围盒，不能调用 setFromObject。
             * 此时仍按参考面的法向、原点和 Y 轴完成正视，绘制投影不依赖可视基准面尺寸。
             */
            this.cameraManager.lookAtTarget(
                resolvedCameraFrame.cameraPosition,
                resolvedCameraFrame.target,
                resolvedCameraFrame.up,
            );
        }
        //------ 基准面正视相机旋转修正 结束------
        this.cameraManager.applyMouseMode();
    }

    //------ 基准面正视相机旋转修正 开始------
    /**
     * 解析基准面默认正视相机帧。
     *
     * 设计说明：
     * 1. SolidWorks 的“正视于”不仅要求视线垂直于草图平面，还要求屏幕上方向跟随草图平面坐标系；
     * 2. 如果只设置相机位置但不设置 up，Three.js 会沿用旧视图旋转，导致草图平面虽然正视但画面仍然歪斜；
     * 3. 因此默认帧统一从 DatumPlane 的 origin / normal / yAxis 推导，保证基准面草图和实体面草图入口一致。
     *
     * @param plane 目标参考面。
     * @returns 默认正视相机帧。
     */
    private resolveDatumPlaneCameraFrame(plane: ReferencePlane): {
        /** 相机位置。 */
        cameraPosition: THREE.Vector3;
        /** 观察目标。 */
        target: THREE.Vector3;
        /** 相机上方向。 */
        up: THREE.Vector3;
    } {
        const target = plane.copyOriginWorldTo(new THREE.Vector3());
        const planeNormal = this.resolveDatumPlaneViewNormal(plane, target);
        const up = this.resolveDatumPlaneCameraUp(plane, planeNormal);
        const cameraPosition = target.clone().add(
            planeNormal.multiplyScalar(DatumPlaneManager.DEFAULT_DATUM_PLANE_VIEW_DISTANCE),
        );

        return {
            cameraPosition,
            target,
            up,
        };
    }

    /**
     * 将相机正视并缩放到完整基准面。
     *
     * 设计说明：
     * 1. 正视于不仅要让视线垂直于基准面，还必须让整个基准面进入视口；
     * 2. 正交相机下屏幕大小由 `camera.zoom` 决定，单纯移动相机无法保证完整入框；
     * 3. 因此这里使用 CameraManager 的包围盒聚焦能力，统一处理透视距离和正交 zoom。
     *
     * @param plane 目标基准面。
     * @param cameraFrame 已解析的正视相机帧。
     */
    private focusCameraToDatumPlaneBounds(
        plane: DatumPlane,
        cameraFrame: {
            /** 相机位置。 */
            cameraPosition: THREE.Vector3;
            /** 观察目标。 */
            target: THREE.Vector3;
            /** 相机上方向。 */
            up: THREE.Vector3;
        },
    ): void {
        if (!this.cameraManager) {
            return;
        }

        const bounds = new THREE.Box3().setFromObject(plane.getObject3D());
        if (bounds.isEmpty()) {
            this.cameraManager.lookAtTarget(
                cameraFrame.cameraPosition,
                cameraFrame.target,
                cameraFrame.up,
            );
            return;
        }

        const direction = cameraFrame.cameraPosition.clone().sub(cameraFrame.target).normalize();
        this.cameraManager.focusOnBounds(bounds, {
            direction,
            up: cameraFrame.up,
        });
    }

    /**
     * 解析进入基准面视图时使用的观察法向。
     *
     * @param plane 目标参考面。
     * @param target 观察目标。
     * @returns 与当前相机所在侧一致的法向。
     */
    private resolveDatumPlaneViewNormal(plane: ReferencePlane, target: THREE.Vector3): THREE.Vector3 {
        const normal = plane.normal.clone().normalize();
        const currentCamera = this.cameraManager?.camera ?? null;
        if (!currentCamera) {
            return normal;
        }

        const cameraSideVector = currentCamera.position.clone().sub(target);
        if (cameraSideVector.lengthSq() <= DatumPlaneManager.AXIS_EPSILON) {
            return normal;
        }

        /**
         * 若当前相机位于平面的反法向一侧，则沿反法向正视。
         * 这样从实体背面插入草图时不会突然翻到另一侧，符合用户当前选择面的视觉语义。
         */
        return cameraSideVector.dot(normal) >= 0
            ? normal
            : normal.negate();
    }

    /**
     * 解析进入基准面视图时使用的相机上方向。
     *
     * @param plane 目标参考面。
     * @param viewNormal 实际观察法向。
     * @returns 与观察法向正交的相机上方向。
     */
    private resolveDatumPlaneCameraUp(plane: ReferencePlane, viewNormal: THREE.Vector3): THREE.Vector3 {
        const projectedPlaneYAxis = this.projectVectorOnPlane(plane.yAxis, viewNormal);
        if (projectedPlaneYAxis) {
            return projectedPlaneYAxis;
        }

        const projectedWorldUp = this.projectVectorOnPlane(new THREE.Vector3(0, 1, 0), viewNormal);
        if (projectedWorldUp) {
            return projectedWorldUp;
        }

        const projectedWorldXAxis = this.projectVectorOnPlane(new THREE.Vector3(1, 0, 0), viewNormal);
        if (projectedWorldXAxis) {
            return projectedWorldXAxis;
        }

        throw new Error('基准面正视失败：无法解析有效的相机上方向。');
    }

    /**
     * 将向量投影到指定平面内。
     *
     * @param vector 输入向量。
     * @param planeNormal 平面法向。
     * @returns 归一化后的平面内向量；退化时返回 null。
     */
    private projectVectorOnPlane(vector: THREE.Vector3, planeNormal: THREE.Vector3): THREE.Vector3 | null {
        const projected = vector
            .clone()
            .sub(planeNormal.clone().multiplyScalar(vector.dot(planeNormal)));
        if (projected.lengthSq() <= DatumPlaneManager.AXIS_EPSILON) {
            return null;
        }
        return projected.normalize();
    }
    //------ 基准面正视相机旋转修正 结束------

    /**
     * 退出基准面视图。
     *
     * 设计说明：
     * 1. 退出草图属于模型会话结束，不应改变用户当前相机位姿；
     * 2. 这里只清理基准面视图状态，让相机停留在用户退出时看到的位置；
     * 3. 若用户需要回到之前视图，应由独立 View Undo / Previous View 负责。
     */
    exitView(): void {
        if (this.viewMode !== ViewMode.DATUM_PLANE) {
            return;
        }
        // 清除参考面和视图模式
        this.currentReferencePlane = null;
        this.viewMode = ViewMode.VIEW_3D;
    }

    /**
     * 根据基准面 ID 同步当前草图参考面。
     * @param planeId 基准面 ID；为空时清空当前参考面。
     */
    setCurrentReferencePlaneByPlaneId(planeId: number | null): void {
        if (planeId === null) {
            this.currentReferencePlane = null;
            return;
        }
        const targetDatumPlane = this.getPlane(planeId);
        this.currentReferencePlane = targetDatumPlane ?? null;
    }

    //------ Undo恢复草图参考面 开始------
    /**
     * 按模型历史恢复草图参考面。
     *
     * 设计说明：
     * 1. Undo/Redo 恢复到草图编辑态时，绘制必须重新绑定草图所属参考面；
     * 2. 该恢复属于模型状态收口，不能调用相机正视入口，否则会污染独立的视图历史；
     * 3. 因此这里只同步参考面和视图模式，不触发 CameraManager。
     *
     * 4. 离线恢复草图持有完整的 ReferencePlane，但该数学参考面不属于可视基准面管理集合；
     * 5. 因此传入完整参考面时，优先复用管理器内同 ID 的规范 DatumPlane，未命中则使用传入对象。
     *
     * @param referencePlaneOrId 草图所属参考面，或仅用于兼容既有调用的基准面 ID。
     * @returns 是否找到并恢复了参考面。
     */
    restoreSketchReferencePlaneFromHistory(
        referencePlaneOrId: ReferencePlane | number,
    ): boolean {
        const referencePlaneId = typeof referencePlaneOrId === 'number'
            ? referencePlaneOrId
            : referencePlaneOrId.id;
        const targetReferencePlane = this.getPlane(referencePlaneId)
            ?? (typeof referencePlaneOrId === 'number' ? null : referencePlaneOrId);
        if (!targetReferencePlane) {
            return false;
        }

        // 恢复绘制投影上下文，但保持当前相机位姿不变。
        this.currentReferencePlane = targetReferencePlane;
        this.viewMode = ViewMode.DATUM_PLANE;
        return true;
    }
    //------ Undo恢复草图参考面 结束------


    /**
     * 清理资源
     */
    dispose(): void {
        this.planes.forEach((plane) => {
            plane.removeFromScene(this.scene);
            plane.dispose();
        });
        this.planes.clear();
    }
}
